import ks from "@shared/kalmstromShared4";
import { IListDefinition, IFieldDefinition, IViewDefinition, IAggregation, IListSettings } from "../DBInterfaces";
import DBState from "../DBState";
import DBModalManager from "../DBModalManager";
import DBOutputSidebar from "../DBOutputSidebar";
import DBStringUtils from "../DBStringUtils";

export default class DBGeneratorPnPXml {

    public static ShowPnPXml = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBModalManager.ShowLoading("Generating PnP XML...", false);
            const xml: string = DBGeneratorPnPXml.GeneratePnPXml();
            DBModalManager.HideLoading();
            if (!xml) { return; }
            const appCode: string = DBState.GetAppCode();
            const filename: string = appCode + "-template.xml";
            DBOutputSidebar.ShowOutputSidebar("PnP Provisioning XML", xml, filename, "application/xml");
        } catch (e) {
            DBModalManager.HideLoading();
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.ShowPnPXml");
        }
    };

    public static GeneratePnPXml = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const appCode: string = DBState.GetAppCode();
            let xml: string = "";
            xml += DBGeneratorPnPXml.GenerateXmlHeader(appCode);
            xml += DBGeneratorPnPXml.GenerateSiteFieldsXml(appCode);
            xml += DBGeneratorPnPXml.GenerateContentTypesXml(appCode);
            xml += DBGeneratorPnPXml.GenerateListInstancesXml(appCode);
            xml += DBGeneratorPnPXml.GenerateXmlFooter();
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GeneratePnPXml");
            return "";
        }
    };

    private static GenerateXmlHeader = (appCode: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let xml: string = "";
            xml += '<?xml version="1.0" encoding="utf-8"?>\n';
            xml += '<pnp:Provisioning xmlns:pnp="http://schemas.dev.office.com/PnP/2022/09/ProvisioningSchema">\n';
            xml += '  <pnp:Templates ID="' + DBStringUtils.EscapeXml(appCode) + '-Templates">\n';
            xml += '    <pnp:ProvisioningTemplate ID="' + DBStringUtils.EscapeXml(appCode) + '-Template" Version="1.0"';
            xml += ' DisplayName="' + DBStringUtils.EscapeXml(appCode) + ' List Schema">\n';
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GenerateXmlHeader");
            return "";
        }
    };

    private static GenerateSiteFieldsXml = (appCode: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const FilterCtEnabled = (list: IListDefinition): boolean => {
                return list.UseContentType === true;
            };
            const ctLists: IListDefinition[] = DBState.Lists.filter(FilterCtEnabled);
            if (ctLists.length === 0) { return ""; }
            let xml: string = "      <pnp:SiteFields>\n";
            const ProcessList = (list: IListDefinition): void => {
                xml += DBGeneratorPnPXml.GenerateSiteFieldsForList(appCode, list);
            };
            ctLists.forEach(ProcessList);
            xml += "      </pnp:SiteFields>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GenerateSiteFieldsXml");
            return "";
        }
    };

    private static IsLookupField = (field: IFieldDefinition): boolean => {
        const xmlType: string = DBGeneratorPnPXml.MapFieldToXmlType(field);
        return xmlType === "Lookup";
    };

    private static GenerateSiteFieldsForList = (appCode: string, list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let xml: string = "";
            const properties: Record<string, IFieldDefinition> = list.Properties;
            if (properties === null || properties === undefined) { return ""; }
            const fieldNames: string[] = Object.keys(properties);
            const ctGroup: string = list.ContentTypeGroup || "Custom Content Types";
            const ProcessField = (fieldName: string): void => {
                const field: IFieldDefinition = properties[fieldName];
                if (DBGeneratorPnPXml.IsLookupField(field)) { return; }
                const fieldXml: string = DBGeneratorPnPXml.GeneratePnPXmlField(appCode, list, fieldName, field);
                const groupAttr: string = ' Group="' + DBStringUtils.EscapeXml(ctGroup) + '"';
                const insertPos: number = fieldXml.indexOf("/>");
                if (insertPos >= 0) {
                    xml += fieldXml.substring(0, insertPos) + groupAttr + " />\n";
                } else {
                    const closeTagPos: number = fieldXml.indexOf(">");
                    if (closeTagPos >= 0) {
                        xml += fieldXml.substring(0, closeTagPos) + groupAttr + fieldXml.substring(closeTagPos);
                    } else {
                        xml += fieldXml;
                    }
                }
            };
            fieldNames.forEach(ProcessField);
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GenerateSiteFieldsForList");
            return "";
        }
    };

    private static GenerateContentTypesXml = (appCode: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const FilterCtEnabled = (list: IListDefinition): boolean => {
                return list.UseContentType === true;
            };
            const ctLists: IListDefinition[] = DBState.Lists.filter(FilterCtEnabled);
            if (ctLists.length === 0) { return ""; }
            let xml: string = "      <pnp:ContentTypes>\n";
            const ProcessList = (list: IListDefinition): void => {
                xml += DBGeneratorPnPXml.GenerateContentTypeForList(appCode, list);
            };
            ctLists.forEach(ProcessList);
            xml += "      </pnp:ContentTypes>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GenerateContentTypesXml");
            return "";
        }
    };

    private static GenerateContentTypeForList = (appCode: string, list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const ctName: string = DBStringUtils.GetContentTypeName(list);
            const parentId: string = "0x01";
            const ctId: string = DBStringUtils.GenerateContentTypeId(appCode, list.Title, ctName, parentId);
            const ctGroup: string = list.ContentTypeGroup || "Custom Content Types";
            let xml: string = "";
            xml += '        <pnp:ContentType ID="' + DBStringUtils.EscapeXml(ctId) + '"';
            xml += ' Name="' + DBStringUtils.EscapeXml(ctName) + '"';
            xml += ' Group="' + DBStringUtils.EscapeXml(ctGroup) + '">\n';
            xml += DBGeneratorPnPXml.GenerateFieldRefsXml(appCode, list);
            xml += "        </pnp:ContentType>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GenerateContentTypeForList");
            return "";
        }
    };

    private static GenerateFieldRefsXml = (appCode: string, list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const properties: Record<string, IFieldDefinition> = list.Properties;
            if (properties === null || properties === undefined) { return ""; }
            const fieldNames: string[] = Object.keys(properties);
            if (fieldNames.length === 0) { return ""; }
            let xml: string = "          <pnp:FieldRefs>\n";
            const ProcessField = (fieldName: string): void => {
                const field: IFieldDefinition = properties[fieldName];
                if (DBGeneratorPnPXml.IsLookupField(field)) { return; }
                const guid: string = DBStringUtils.GenerateDeterministicGuid(appCode + "_" + list.Title + "_" + fieldName);
                xml += '            <pnp:FieldRef ID="' + DBStringUtils.EscapeXml(guid) + '"';
                xml += ' Name="' + DBStringUtils.EscapeXml(fieldName) + '" />\n';
            };
            fieldNames.forEach(ProcessField);
            xml += "          </pnp:FieldRefs>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GenerateFieldRefsXml");
            return "";
        }
    };

    private static GenerateListInstancesXml = (appCode: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (DBState.Lists.length === 0) { return ""; }
            let xml: string = "      <pnp:Lists>\n";
            const ProcessList = (list: IListDefinition): void => {
                xml += DBGeneratorPnPXml.GenerateListInstanceXml(appCode, list);
            };
            DBState.Lists.forEach(ProcessList);
            xml += "      </pnp:Lists>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GenerateListInstancesXml");
            return "";
        }
    };

    private static GenerateListInstanceXml = (appCode: string, list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let xml: string = "";
            xml += DBGeneratorPnPXml.BuildListInstanceOpenTag(list);
            xml += DBGeneratorPnPXml.BuildListInstanceBody(appCode, list);
            xml += "        </pnp:ListInstance>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GenerateListInstanceXml");
            return "";
        }
    };

    private static BuildListInstanceOpenTag = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let xml: string = '        <pnp:ListInstance Title="' + DBStringUtils.EscapeXml(list.Title) + '"';
            xml += ' TemplateType="' + (list.ListTemplate || 100) + '"';
            xml += ' Url="Lists/' + DBStringUtils.EscapeXml(list.Title) + '"';
            xml += DBGeneratorPnPXml.BuildSettingsAttributes(list.Settings);
            xml += ">\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildListInstanceOpenTag");
            return "";
        }
    };

    private static BuildSettingsAttributes = (settings: IListSettings | null): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (settings === null || settings === undefined) { return ""; }
            let attrs: string = "";
            if (settings.EnableVersioning === true) { attrs += ' EnableVersioning="true"'; }
            if (settings.MajorVersionLimit > 0) { attrs += ' MaxVersionLimit="' + settings.MajorVersionLimit + '"'; }
            if (settings.EnableMinorVersions === true) { attrs += ' EnableMinorVersions="true"'; }
            if (settings.EnableModeration === true) { attrs += ' EnableModeration="true"'; }
            if (settings.EnableAttachments === false) { attrs += ' EnableAttachments="false"'; }
            if (settings.EnableFolderCreation === true) { attrs += ' EnableFolderCreation="true"'; }
            if (settings.ContentTypesEnabled === true) { attrs += ' ContentTypesEnabled="true"'; }
            if (settings.ForceCheckout === true) { attrs += ' ForceCheckout="true"'; }
            if (settings.Hidden === true) { attrs += ' Hidden="true"'; }
            if (settings.OnQuickLaunch === false) { attrs += ' OnQuickLaunch="false"'; }
            if (settings.DisableGridEditing === true) { attrs += ' DisableGridEditing="true"'; }
            if (settings.ReadSecurity > 0) { attrs += ' ReadSecurity="' + settings.ReadSecurity + '"'; }
            if (settings.WriteSecurity > 0) { attrs += ' WriteSecurity="' + settings.WriteSecurity + '"'; }
            return attrs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildSettingsAttributes");
            return "";
        }
    };

    private static BuildListInstanceBody = (appCode: string, list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let xml: string = "";
            if (list.UseContentType === true) {
                xml += DBGeneratorPnPXml.BuildContentTypeBindings(appCode, list);
                xml += DBGeneratorPnPXml.BuildLookupFieldsSection(appCode, list);
            } else {
                xml += DBGeneratorPnPXml.BuildFieldsSection(appCode, list);
            }
            xml += DBGeneratorPnPXml.BuildViewsSection(list);
            xml += DBGeneratorPnPXml.BuildDataRowsSection(list);
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildListInstanceBody");
            return "";
        }
    };

    private static BuildContentTypeBindings = (appCode: string, list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const ctName: string = DBStringUtils.GetContentTypeName(list);
            const parentId: string = "0x01";
            const ctId: string = DBStringUtils.GenerateContentTypeId(appCode, list.Title, ctName, parentId);
            let xml: string = "          <pnp:ContentTypeBindings>\n";
            xml += '            <pnp:ContentTypeBinding ContentTypeID="' + DBStringUtils.EscapeXml(ctId) + '" Default="true" />\n';
            xml += "          </pnp:ContentTypeBindings>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildContentTypeBindings");
            return "";
        }
    };

    private static BuildFieldsSection = (appCode: string, list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const properties: Record<string, IFieldDefinition> = list.Properties;
            if (properties === null || properties === undefined) { return ""; }
            const fieldNames: string[] = Object.keys(properties);
            if (fieldNames.length === 0) { return ""; }
            let xml: string = "          <pnp:Fields>\n";
            const ProcessField = (fieldName: string): void => {
                const field: IFieldDefinition = properties[fieldName];
                xml += "            " + DBGeneratorPnPXml.GeneratePnPXmlField(appCode, list, fieldName, field) + "\n";
            };
            fieldNames.forEach(ProcessField);
            xml += "          </pnp:Fields>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildFieldsSection");
            return "";
        }
    };

    private static BuildLookupFieldsSection = (appCode: string, list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const properties: Record<string, IFieldDefinition> = list.Properties;
            if (properties === null || properties === undefined) { return ""; }
            const fieldNames: string[] = Object.keys(properties);
            const lookupNames: string[] = fieldNames.filter((name: string) => {
                return DBGeneratorPnPXml.IsLookupField(properties[name]);
            });
            if (lookupNames.length === 0) { return ""; }
            let xml: string = "          <pnp:Fields>\n";
            const ProcessField = (fieldName: string): void => {
                const field: IFieldDefinition = properties[fieldName];
                xml += "            " + DBGeneratorPnPXml.GeneratePnPXmlField(appCode, list, fieldName, field) + "\n";
            };
            lookupNames.forEach(ProcessField);
            xml += "          </pnp:Fields>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildLookupFieldsSection");
            return "";
        }
    };

    private static BuildViewsSection = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const views: IViewDefinition[] = list.Views;
            if (views === null || views === undefined || views.length === 0) { return ""; }
            let xml: string = "          <pnp:Views>\n";
            const ProcessView = (view: IViewDefinition): void => {
                xml += DBGeneratorPnPXml.GeneratePnPXmlView(view);
            };
            views.forEach(ProcessView);
            xml += "          </pnp:Views>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildViewsSection");
            return "";
        }
    };

    private static BuildDataRowsSection = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const data: Record<string, string>[] = list.Data;
            if (data === null || data === undefined || data.length === 0) { return ""; }
            let xml: string = '          <pnp:DataRows KeyColumn="Title" UpdateBehavior="Overwrite">\n';
            const ProcessRow = (row: Record<string, string>): void => {
                xml += DBGeneratorPnPXml.BuildDataRow(row);
            };
            data.forEach(ProcessRow);
            xml += "          </pnp:DataRows>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildDataRowsSection");
            return "";
        }
    };

    private static BuildDataRow = (row: Record<string, string>): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let xml: string = "            <pnp:DataRow>\n";
            const keys: string[] = Object.keys(row);
            const ProcessKey = (key: string): void => {
                const value: string = row[key];
                xml += '              <pnp:DataValue FieldName="' + DBStringUtils.EscapeXml(key) + '">';
                xml += DBStringUtils.EscapeXml(value) + '</pnp:DataValue>\n';
            };
            keys.forEach(ProcessKey);
            xml += "            </pnp:DataRow>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildDataRow");
            return "";
        }
    };

    private static GenerateXmlFooter = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let xml: string = "";
            xml += "    </pnp:ProvisioningTemplate>\n";
            xml += "  </pnp:Templates>\n";
            xml += "</pnp:Provisioning>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GenerateXmlFooter");
            return "";
        }
    };

    public static GeneratePnPXmlField = (appCode: string, list: IListDefinition, internalName: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const xmlType: string = DBGeneratorPnPXml.MapFieldToXmlType(field);
            const displayName: string = field.Title || internalName;
            const isRequired: boolean = field.Required === true;
            const guid: string = DBStringUtils.GenerateDeterministicGuid(appCode + "_" + list.Title + "_" + internalName);
            let attrs: string = DBGeneratorPnPXml.BuildFieldAttributes(guid, internalName, displayName, xmlType, isRequired);
            attrs = DBGeneratorPnPXml.AddTypeSpecificAttributes(attrs, xmlType, field);
            attrs = DBGeneratorPnPXml.AddCommonOptionalAttributes(attrs, field);
            const childElements: string = DBGeneratorPnPXml.BuildFieldChildElements(xmlType, field);
            if (childElements) {
                return "<Field" + attrs + ">" + childElements + "</Field>";
            }
            return "<Field" + attrs + " />";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GeneratePnPXmlField");
            return "";
        }
    };

    public static MapFieldToXmlType = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return "Text"; }
        try {
            const fieldType: string = field.Format || field.Type;
            if (fieldType === "note" || fieldType === "rich-text") { return "Note"; }
            if (fieldType === "number") { return "Number"; }
            if (fieldType === "currency") { return "Currency"; }
            if (fieldType === "integer") { return "Integer"; }
            if (fieldType === "boolean") { return "Boolean"; }
            if (fieldType === "date-time" || fieldType === "date") { return "DateTime"; }
            if (fieldType === "choice") { return "Choice"; }
            if (fieldType === "string" && field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) { return "Choice"; }
            if (fieldType === "multichoice") { return "MultiChoice"; }
            if (fieldType === "array" && field.Items !== null && field.Items !== undefined && field.Items.Enum !== null && field.Items.Enum !== undefined && field.Items.Enum.length > 0) { return "MultiChoice"; }
            if (fieldType === "lookup") { return "Lookup"; }
            if (fieldType === "user") { return "User"; }
            if (fieldType === "url") { return "URL"; }
            if (fieldType === "geolocation") { return "Geolocation"; }
            return "Text";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.MapFieldToXmlType");
            return "Text";
        }
    };

    private static BuildFieldAttributes = (guid: string, internalName: string, displayName: string, xmlType: string, isRequired: boolean): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let attrs: string = "";
            attrs += ' ID="' + DBStringUtils.EscapeXml(guid) + '"';
            attrs += ' Name="' + DBStringUtils.EscapeXml(internalName) + '"';
            attrs += ' DisplayName="' + DBStringUtils.EscapeXml(displayName) + '"';
            attrs += ' Type="' + DBStringUtils.EscapeXml(xmlType) + '"';
            attrs += ' Required="' + (isRequired ? "TRUE" : "FALSE") + '"';
            return attrs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildFieldAttributes");
            return "";
        }
    };

    private static AddTypeSpecificAttributes = (attrs: string, xmlType: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return attrs; }
        try {
            if (xmlType === "Text") {
                attrs = DBGeneratorPnPXml.AddTextAttributes(attrs, field);
            } else if (xmlType === "Note") {
                attrs = DBGeneratorPnPXml.AddNoteAttributes(attrs, field);
            } else if (xmlType === "Number") {
                attrs = DBGeneratorPnPXml.AddNumberAttributes(attrs, field);
            } else if (xmlType === "DateTime") {
                attrs = DBGeneratorPnPXml.AddDateTimeAttributes(attrs, field);
            } else if (xmlType === "Lookup") {
                attrs = DBGeneratorPnPXml.AddLookupAttributes(attrs, field);
            } else if (xmlType === "User") {
                attrs = DBGeneratorPnPXml.AddUserAttributes(attrs, field);
            } else if (xmlType === "Choice" || xmlType === "MultiChoice") {
                attrs = DBGeneratorPnPXml.AddChoiceAttributes(attrs, field);
            }
            return attrs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.AddTypeSpecificAttributes");
            return attrs;
        }
    };

    private static AddTextAttributes = (attrs: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return attrs; }
        try {
            if (field.MaxLength > 0) {
                attrs += ' MaxLength="' + field.MaxLength + '"';
            }
            return attrs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.AddTextAttributes");
            return attrs;
        }
    };

    private static AddNoteAttributes = (attrs: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return attrs; }
        try {
            if (field.NumberOfLines > 0) {
                attrs += ' NumLines="' + field.NumberOfLines + '"';
            }
            if (field.RichText === true) {
                attrs += ' RichText="TRUE"';
            }
            if (field.AppendOnly === true) {
                attrs += ' AppendOnly="TRUE"';
            }
            return attrs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.AddNoteAttributes");
            return attrs;
        }
    };

    private static AddNumberAttributes = (attrs: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return attrs; }
        try {
            if (field.MinimumValue !== null && field.MinimumValue !== undefined) {
                attrs += ' Min="' + field.MinimumValue + '"';
            }
            if (field.MaximumValue !== null && field.MaximumValue !== undefined) {
                attrs += ' Max="' + field.MaximumValue + '"';
            }
            if (field.DisplayFormat) {
                attrs += ' Decimals="' + DBStringUtils.EscapeXml(field.DisplayFormat) + '"';
            }
            if (field.ShowAsPercentage === true) {
                attrs += ' Percentage="TRUE"';
            }
            return attrs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.AddNumberAttributes");
            return attrs;
        }
    };

    private static AddDateTimeAttributes = (attrs: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return attrs; }
        try {
            if (field.Format) {
                attrs += ' Format="' + DBStringUtils.EscapeXml(field.Format) + '"';
            }
            if (field.FriendlyDisplayFormat) {
                attrs += ' FriendlyDisplayFormat="' + DBStringUtils.EscapeXml(field.FriendlyDisplayFormat) + '"';
            }
            return attrs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.AddDateTimeAttributes");
            return attrs;
        }
    };

    private static AddLookupAttributes = (attrs: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return attrs; }
        try {
            if (field.LookupList) {
                attrs += ' List="{listid:' + DBStringUtils.EscapeXml(field.LookupList) + '}"';
            }
            if (field.LookupField) {
                attrs += ' ShowField="' + DBStringUtils.EscapeXml(field.LookupField) + '"';
            }
            if (field.AllowMultipleValues === true) {
                attrs += ' Mult="TRUE"';
            }
            return attrs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.AddLookupAttributes");
            return attrs;
        }
    };

    private static AddUserAttributes = (attrs: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return attrs; }
        try {
            if (field.SelectionMode) {
                attrs += ' UserSelectionMode="' + DBStringUtils.EscapeXml(field.SelectionMode) + '"';
            }
            if (field.AllowMultipleValues === true) {
                attrs += ' Mult="TRUE"';
            }
            return attrs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.AddUserAttributes");
            return attrs;
        }
    };

    private static AddChoiceAttributes = (attrs: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return attrs; }
        try {
            if (field.FillInChoice === true) {
                attrs += ' FillInChoice="TRUE"';
            }
            return attrs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.AddChoiceAttributes");
            return attrs;
        }
    };

    private static AddCommonOptionalAttributes = (attrs: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return attrs; }
        try {
            if (field.EnforceUniqueValues === true) {
                attrs += ' EnforceUniqueValues="TRUE"';
            }
            if (field.Description) {
                attrs += ' Description="' + DBStringUtils.EscapeXml(field.Description) + '"';
            }
            if (field.ShowInDisplayForm === false) {
                attrs += ' ShowInDisplayForm="FALSE"';
            }
            if (field.ShowInEditForm === false) {
                attrs += ' ShowInEditForm="FALSE"';
            }
            if (field.ShowInNewForm === false) {
                attrs += ' ShowInNewForm="FALSE"';
            }
            attrs = DBGeneratorPnPXml.AddValidationAttributes(attrs, field);
            return attrs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.AddCommonOptionalAttributes");
            return attrs;
        }
    };

    private static AddValidationAttributes = (attrs: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return attrs; }
        try {
            if (field.ValidationFormula) {
                attrs += ' ValidationFormula="' + DBStringUtils.EscapeXml(field.ValidationFormula) + '"';
            }
            if (field.ValidationMessage) {
                attrs += ' ValidationMessage="' + DBStringUtils.EscapeXml(field.ValidationMessage) + '"';
            }
            return attrs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.AddValidationAttributes");
            return attrs;
        }
    };

    private static BuildFieldChildElements = (xmlType: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let children: string = "";
            children += DBGeneratorPnPXml.BuildDefaultValueElement(field);
            children += DBGeneratorPnPXml.BuildChoicesElement(xmlType, field);
            return children;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildFieldChildElements");
            return "";
        }
    };

    private static BuildDefaultValueElement = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (!field.DefaultValue) { return ""; }
            return "<Default>" + DBStringUtils.EscapeXml(field.DefaultValue) + "</Default>";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildDefaultValueElement");
            return "";
        }
    };

    private static BuildChoicesElement = (xmlType: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const choices: string[] | null = DBGeneratorPnPXml.GetChoicesArray(xmlType, field);
            if (choices === null || choices.length === 0) { return ""; }
            let xml: string = "<CHOICES>";
            const ProcessChoice = (choice: string): void => {
                xml += "<CHOICE>" + DBStringUtils.EscapeXml(choice) + "</CHOICE>";
            };
            choices.forEach(ProcessChoice);
            xml += "</CHOICES>";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildChoicesElement");
            return "";
        }
    };

    private static GetChoicesArray = (xmlType: string, field: IFieldDefinition): string[] | null => {
        if (ks.kErrorMode === true) { return null; }
        try {
            if (xmlType !== "Choice" && xmlType !== "MultiChoice") { return null; }
            if (field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) {
                return field.Enum;
            }
            if (field.Items !== null && field.Items !== undefined && field.Items.Enum !== null && field.Items.Enum !== undefined && field.Items.Enum.length > 0) {
                return field.Items.Enum;
            }
            return null;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GetChoicesArray");
            return null;
        }
    };

    private static GeneratePnPXmlView = (view: IViewDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let xml: string = DBGeneratorPnPXml.BuildViewOpenTag(view);
            xml += DBGeneratorPnPXml.BuildViewFieldsXml(view);
            xml += DBGeneratorPnPXml.BuildViewQueryXml(view);
            xml += DBGeneratorPnPXml.BuildViewAggregationsXml(view);
            xml += DBGeneratorPnPXml.BuildViewCustomFormatterXml(view);
            xml += "            </View>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.GeneratePnPXmlView");
            return "";
        }
    };

    private static BuildViewOpenTag = (view: IViewDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let xml: string = "            <View";
            xml += ' DisplayName="' + DBStringUtils.EscapeXml(view.Title) + '"';
            xml += ' DefaultView="' + (view.IsDefault === true ? "TRUE" : "FALSE") + '"';
            xml += ' Paged="' + (view.Paged === true ? "TRUE" : "FALSE") + '"';
            xml += ' RowLimit="' + (view.RowLimit || 30) + '"';
            if (view.Hidden === true) { xml += ' Hidden="TRUE"'; }
            if (view.ViewType) { xml += ' Type="' + DBStringUtils.EscapeXml(view.ViewType) + '"'; }
            if (view.Scope) { xml += ' Scope="' + DBStringUtils.EscapeXml(view.Scope) + '"'; }
            xml += ">\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildViewOpenTag");
            return "";
        }
    };

    private static BuildViewFieldsXml = (view: IViewDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const viewFields: string[] = view.ViewFields;
            if (viewFields === null || viewFields === undefined || viewFields.length === 0) { return ""; }
            let xml: string = "              <ViewFields>\n";
            const ProcessField = (fieldName: string): void => {
                xml += '                <FieldRef Name="' + DBStringUtils.EscapeXml(fieldName) + '" />\n';
            };
            viewFields.forEach(ProcessField);
            xml += "              </ViewFields>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildViewFieldsXml");
            return "";
        }
    };

    private static BuildViewQueryXml = (view: IViewDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (!view.ViewQuery) { return ""; }
            let xml: string = "              <Query>\n";
            xml += "                " + view.ViewQuery + "\n";
            xml += "              </Query>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildViewQueryXml");
            return "";
        }
    };

    private static BuildViewAggregationsXml = (view: IViewDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const aggregations: IAggregation[] = view.Aggregations;
            if (aggregations === null || aggregations === undefined || aggregations.length === 0) { return ""; }
            const validAggs: IAggregation[] = aggregations.filter((agg: IAggregation) => {
                return agg.FieldName !== null && agg.FieldName !== undefined && agg.FieldName !== ""
                    && agg.Function !== null && agg.Function !== undefined && agg.Function !== "";
            });
            if (validAggs.length === 0) { return ""; }
            let xml: string = '              <Aggregations Value="On">\n';
            const ProcessAgg = (agg: IAggregation): void => {
                xml += '                <FieldRef Name="' + DBStringUtils.EscapeXml(agg.FieldName) + '"';
                xml += ' Type="' + DBStringUtils.EscapeXml(agg.Function) + '" />\n';
            };
            validAggs.forEach(ProcessAgg);
            xml += "              </Aggregations>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildViewAggregationsXml");
            return "";
        }
    };

    private static BuildViewCustomFormatterXml = (view: IViewDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (!view.CustomFormatter) { return ""; }
            let xml: string = "              <CustomFormatter>\n";
            xml += "                " + DBStringUtils.EscapeXml(view.CustomFormatter) + "\n";
            xml += "              </CustomFormatter>\n";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPnPXml.BuildViewCustomFormatterXml");
            return "";
        }
    };
}
